import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { UpdateQboNumberButton } from './jobs/InvoiceNumberBadge';
import { RepeatInvoiceDialog } from './RecurringInvoices';
import { Repeat } from 'lucide-react';
import {
  Loader2, Send, Download, Mail, FileText, Save, AlertCircle,
  Percent, MapPin, Info, Hash,
} from 'lucide-react';
import QbPushReviewDialog from './QbPushReviewDialog';
import InvoiceMaterialsEditor from './invoice/InvoiceMaterialsEditor';
import InvoiceChargesEditor from './invoice/InvoiceChargesEditor';
import { CustomColumnInputs } from './ColumnLayoutEditor';
import { EditableHeader } from './DailyInvoiceBits';
import InvoiceQbParityBlock from './invoice/InvoiceQbParityBlock';
import InvoiceCurrencyPresentation from './invoice/InvoiceCurrencyPresentation';

const API = `${window.__BACKEND_URL__}/api`;

// Format an amount with the proper currency symbol. Each instance of the
// editor receives the resolved company currency in the preview payload and
// passes it down — never hardcode "$" so CAD/USD/EUR/etc. all render right.
const moneyIn = (n, currency = 'USD') => {
  const num = Number(n) || 0;
  try {
    // Explicit prefixes (CA$/US$) so multi-currency invoices are never ambiguous — matches the PDF.
    const prefix = { USD: 'US$', CAD: 'CA$', AUD: 'A$', NZD: 'NZ$', MXN: 'MX$' }[(currency || 'USD').toUpperCase()];
    if (prefix) return `${prefix}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return num.toLocaleString(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

/**
 * InvoiceEditorDialog (Phase 2 — Feb 2026)
 * -----------------------------------------
 * Bill-out hub for any job status (pending/in_progress/paused/completed).
 * Admin fixes rate, materials (with cost + markup), tax preset, memo, then
 * chooses: Send to QuickBooks / Download PDF / Email PDF.
 *
 * Phase 2 additions over Phase 1:
 *   - Tax preset dropdown (built-in CA/US + company custom). Auto-suggested
 *     from job address. Multi-component presets (QC GST+QST) render as
 *     separate lines in the totals card.
 *   - Material rows now have Cost + Unit Price + Markup % columns. Editing
 *     Cost auto-fills Unit Price = cost × (1 + effective_markup). Editing
 *     Unit Price unlocks the row from the markup.
 *   - Effective markup banner: shows company default + customer override +
 *     this-job override. Admin can set a per-job override.
 */
export default function InvoiceEditorDialog({ open, onClose, job, token, onInvoiced }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [data, setData] = useState(null);

  const [serviceRate, setServiceRate] = useState('');
  const [billingMode, setBillingMode] = useState('hourly');
  const [taxPresetId, setTaxPresetId] = useState('');   // '' or 'none' = no preset (use legacy single-line tax)
  const [memo, setMemo] = useState('');
  const [currencyOverride, setCurrencyOverride] = useState('');
  const [presentation, setPresentation] = useState('itemized');
  const [summary, setSummary] = useState('');
  const [materials, setMaterials] = useState([]);
  // Optional invoice charges (Feb 2026) — Travel, Fuel Surcharge,
  // Tools & Equipment, Other. Admin picks from a dropdown to drop in
  // a pre-filled row, then edits in place. Persisted alongside materials
  // and pushed to QBO as additional SalesItemLine entries.
  const [charges, setCharges] = useState([]);
  const [labourCustom, setLabourCustom] = useState({});
  // Per-job header renames (printed on the PDF/XLSX) — {column key: label}. Sep 2026.
  const [colLabels, setColLabels] = useState({});
  const docColumns = (data?.columns || []).map((c) => (colLabels[c.key] ? { ...c, label: colLabels[c.key] } : c));
  // Suggestion bag the "Add optional charge" dropdown opens with —
  // fetched from /api/jobs/{id}/invoice/suggested-charges. Travel comes
  // pre-priced from the job's closed travel segments × labor rate.
  const [chargeSuggestions, setChargeSuggestions] = useState([]);
  const [chargeMenuOpen, setChargeMenuOpen] = useState(false);
  const [markupOverride, setMarkupOverride] = useState(''); // per-job override of effective markup
  // Phase C (Feb 2026, QuickBooks parity) — separate visible vs. hidden
  // invoice memos + labor-line QB Class override. Per-line material QB
  // mappings are saved opportunistically when admins set them via the
  // (future) materials sub-editor; for now this dialog edits invoice-level
  // overrides only.
  const [noteToCustomer, setNoteToCustomer] = useState('');
  const [memoOnStatement, setMemoOnStatement] = useState('');
  // PO Number + Work Order Number (Feb 2026, Review & Push dialog). PO Number
  // flows to QBO's native Invoice.PONumber field; Work Order Number rides
  // in PrivateNote when admin includes it via the push dialog.
  const [poNumber, setPoNumber] = useState('');
  const [workOrderNumber, setWorkOrderNumber] = useState('');
  const [laborClassId, setLaborClassId] = useState('');
  const [laborClassName, setLaborClassName] = useState('');
  const [qbClasses, setQbClasses] = useState([]); // populated from /api/qb/classes
  // Per-line QBO Item picker (Feb 2026) — `qb_item_id` per material lets
  // admins override the Product/Service column in the QBO invoice on a
  // line-by-line basis. Backend already honours it in sync_job_to_invoice.
  const [qbItems, setQbItems] = useState([]); // populated from /api/qb/items
  // Filter materials view by who entered them (click on a worker chip in the
  // "Sourced by" snapshot). null = show all. Pure UI state — no backend change.
  const [filterByWorker, setFilterByWorker] = useState(null);

  // Reset + fetch every time the dialog opens with a new job
  useEffect(() => {
    if (!open || !job?.id) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    (async () => {
      try {
        const res = await axios.get(`${API}/jobs/${job.id}/invoice-preview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const j = res.data?.job || {};
        setData(res.data);
        setServiceRate(j.service_rate != null ? String(j.service_rate) : '');
        setBillingMode(j.billing_mode || 'hourly');
        setMemo(j.invoice_memo || '');
        setCurrencyOverride(j.invoice_currency || '');
        setPresentation(j.invoice_presentation || 'itemized');
        setSummary(j.invoice_summary || '');
        setNoteToCustomer(j.invoice_note_to_customer || '');
        setMemoOnStatement(j.invoice_memo_on_statement || '');
        setPoNumber(j.customer_po_number || '');
        setWorkOrderNumber(j.work_order_number || '');
        setLaborClassId(j.invoice_labor_qb_class_id || '');
        setLaborClassName(j.invoice_labor_qb_class_name || '');
        setMaterials(Array.isArray(j.materials) ? j.materials.map(m => ({ ...m })) : []);
        setCharges(Array.isArray(j.invoice_charges) ? j.invoice_charges.map(c => ({ ...c })) : []);
        setLabourCustom(j.invoice_labour_custom_fields || {});
        setColLabels(Array.isArray(j.invoice_columns) && j.invoice_columns.length ? Object.fromEntries(j.invoice_columns.map((c) => [c.key, c.label])) : {});
        // Suggestion bag for the "Add optional charge" dropdown. Travel
        // entry is pre-priced from the job's closed segments × labor rate
        // so a one-click drop-in already shows the right total.
        axios.get(`${API}/jobs/${job.id}/invoice/suggested-charges`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(({ data }) => { if (!cancelled) setChargeSuggestions(data?.suggestions || []); })
          .catch(() => { if (!cancelled) setChargeSuggestions([]); });
        // Fire-and-forget: pull cached QB Classes for the labor-line picker.
        // Empty list is fine — admin can sync from Settings → Projects.
        axios.get(`${API}/qb/classes`, { headers: { Authorization: `Bearer ${token}` } })
          .then(({ data }) => { if (!cancelled) setQbClasses(Array.isArray(data) ? data : []); })
          .catch(() => { if (!cancelled) setQbClasses([]); });
        // QBO Product/Service items for the per-line Item picker.
        axios.get(`${API}/qb/items`, { headers: { Authorization: `Bearer ${token}` } })
          .then(({ data }) => { if (!cancelled) setQbItems(Array.isArray(data) ? data : []); })
          .catch(() => { if (!cancelled) setQbItems([]); });
        setMarkupOverride(j.material_markup_pct != null ? String(j.material_markup_pct) : '');
        // Tax preset: explicit > suggested > 'none'
        const explicit = j.invoice_tax_preset_id;
        const suggested = res.data?.suggested_tax_preset?.id;
        setTaxPresetId(explicit || suggested || 'none');
      } catch (err) {
        toast({
          title: 'Could not load invoice preview',
          description: err.response?.data?.detail || err.message,
          variant: 'destructive',
        });
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, job?.id, token, onClose, toast]);

  // Phase 2 (Feb 2026) — Worker contributions snapshot. Groups materials by
  // who entered them so admins can see at a glance who sourced what
  // (commission payouts, audit, "did Mike actually do this site visit?").
  const workerContributions = useMemo(() => {
    if (!materials?.length) return [];
    const byWorker = new Map();
    for (const m of materials) {
      const who = (m.added_by_name || '').trim();
      if (!who) continue;
      const q = parseFloat(m.quantity) || 0;
      // Use cost (what they paid) when present; fall back to unit_price.
      const basis = m.cost != null && m.cost !== '' ? parseFloat(m.cost) : parseFloat(m.unit_price) || 0;
      const amount = q * (Number.isFinite(basis) ? basis : 0);
      const prev = byWorker.get(who) || { name: who, total: 0, count: 0 };
      prev.total += amount;
      prev.count += 1;
      byWorker.set(who, prev);
    }
    return [...byWorker.values()].sort((a, b) => b.total - a.total);
  }, [materials]);

  // Effective markup % — 4-level resolution (line override happens in rows below)
  const effectiveMarkup = useMemo(() => {
    if (markupOverride !== '') return parseFloat(markupOverride) || 0;
    const cust = data?.customer?.material_markup_pct;
    if (cust != null) return parseFloat(cust) || 0;
    return parseFloat(data?.company?.material_markup_pct) || 0;
  }, [markupOverride, data]);

  const markupSource = useMemo(() => {
    if (markupOverride !== '') return 'this invoice';
    if (data?.customer?.material_markup_pct != null) return `customer ${data?.customer?.name || ''}`;
    return 'company default';
  }, [markupOverride, data]);

  // Selected preset object (or null = no preset)
  const selectedPreset = useMemo(() => {
    if (!data) return null;
    if (taxPresetId === 'none' || !taxPresetId) return null;
    return (data.tax_presets || []).find(p => p.id === taxPresetId) || null;
  }, [data, taxPresetId]);

  // Live totals — local computation so the user sees instant feedback.
  // Backend recomputes authoritatively on every Save (PATCH).
  const localTotals = useMemo(() => {
    if (!data) return null;
    const rate = parseFloat(serviceRate) || 0;
    const hours = Number(data.job?.total_hours_worked || 0);
    const mode = (billingMode || 'hourly').toLowerCase();
    const useFlat = mode === 'flat' || rate >= 1000 || hours <= 0;
    const labor = useFlat ? rate : +(rate * hours).toFixed(2);
    let matTotal = 0;
    let matCostTotal = 0;
    for (const m of materials) {
      const q = parseFloat(m.quantity) || 0;
      const up = parseFloat(m.unit_price) || 0;
      const cost = m.cost != null && m.cost !== '' ? parseFloat(m.cost) : NaN;
      matTotal += q * up;
      if (Number.isFinite(cost)) matCostTotal += q * cost;
    }
    // Optional invoice charges total (Feb 2026)
    let chargesTotal = 0;
    for (const c of charges) {
      const q = parseFloat(c.quantity) || 0;
      const up = parseFloat(c.unit_price) || 0;
      chargesTotal += q * up;
    }
    const subtotal = +(labor + matTotal + chargesTotal).toFixed(2);
    let taxComponents = [];
    if (selectedPreset && Array.isArray(selectedPreset.components)) {
      taxComponents = selectedPreset.components
        .filter(c => Number(c.rate_pct || 0) > 0)
        .map(c => ({
          label: c.label || 'Tax',
          rate_pct: Number(c.rate_pct) || 0,
          amount: +(subtotal * Number(c.rate_pct) / 100).toFixed(2),
        }));
    } else {
      // Legacy single-line fallback (company default)
      const pct = Number(data?.company?.invoice_tax_rate) || 0;
      if (pct > 0) {
        taxComponents = [{
          label: data?.company?.invoice_tax_label || 'Tax',
          rate_pct: pct,
          amount: +(subtotal * pct / 100).toFixed(2),
        }];
      }
    }
    const taxAmount = +taxComponents.reduce((s, c) => s + c.amount, 0).toFixed(2);
    const grand = +(subtotal + taxAmount).toFixed(2);
    return {
      labor, materialsTotal: +matTotal.toFixed(2), subtotal,
      materialsCostTotal: +matCostTotal.toFixed(2),
      chargesTotal: +chargesTotal.toFixed(2),
      taxComponents, taxAmount, grandTotal: grand,
      hours, useFlat,
    };
  }, [data, serviceRate, billingMode, materials, charges, selectedPreset]);

  // Apply worker filter to the materials grid. Indexes are preserved by
  // tracking the row's id so updateMaterial(idx,..) keeps working regardless
  // of filter state.
  const visibleMaterials = useMemo(() => {
    if (!filterByWorker) return materials.map((m, i) => ({ ...m, _origIdx: i }));
    return materials
      .map((m, i) => ({ ...m, _origIdx: i }))
      .filter(m => (m.added_by_name || '') === filterByWorker);
  }, [materials, filterByWorker]);

  const addMaterial = () => setMaterials(m => [...m, {
    id: `tmp-${Date.now()}-${m.length}`, description: '', quantity: 1,
    cost: '', unit_price: 0, markup_pct: null,
  }]);
  const removeMaterial = (idx) => setMaterials(m => m.filter((_, i) => i !== idx));
  const updateMaterial = (idx, field, value) => setMaterials(m => m.map((row, i) => {
    if (i !== idx) return row;
    const next = { ...row, [field]: value };
    // When admin edits Cost, auto-fill Unit Price = cost × (1 + markup).
    // The row-level markup_pct overrides the effective markup if set.
    if (field === 'cost') {
      const cost = parseFloat(value);
      if (!isNaN(cost) && cost > 0) {
        const rowMarkup = row.markup_pct != null && row.markup_pct !== '' ? parseFloat(row.markup_pct) : effectiveMarkup;
        next.unit_price = +(cost * (1 + (rowMarkup || 0) / 100)).toFixed(2);
      }
    }
    // When admin edits per-row markup_pct, recompute unit_price from cost.
    if (field === 'markup_pct') {
      const cost = parseFloat(row.cost);
      const rowMarkup = value === '' || value == null ? effectiveMarkup : parseFloat(value);
      if (!isNaN(cost) && cost > 0) {
        next.unit_price = +(cost * (1 + (rowMarkup || 0) / 100)).toFixed(2);
      }
    }
    return next;
  }));

  // Optional invoice charges helpers (Feb 2026)
  const addCharge = (suggestion = null) => {
    setCharges((cs) => [...cs, {
      id: `tmp-${Date.now()}-${cs.length}`,
      kind: suggestion?.kind || 'other',
      description: suggestion?.description || '',
      quantity: suggestion?.quantity ?? 1,
      unit_price: suggestion?.unit_price ?? 0,
    }]);
    setChargeMenuOpen(false);
  };
  const removeCharge = (idx) => setCharges((cs) => cs.filter((_, i) => i !== idx));
  const updateCharge = (idx, field, value) => setCharges((cs) => cs.map((row, i) =>
    i === idx ? { ...row, [field]: value } : row
  ));


  const buildPayload = () => ({
    service_rate: serviceRate === '' ? null : parseFloat(serviceRate),
    billing_mode: billingMode,
    invoice_memo: memo || null,
    invoice_currency: currencyOverride || null,
    invoice_presentation: presentation,
    invoice_summary: summary || null,
    invoice_labour_custom_fields: labourCustom,
    invoice_columns: Object.keys(colLabels).length ? docColumns : null,
    invoice_tax_preset_id: (taxPresetId === 'none' || !taxPresetId) ? null : taxPresetId,
    // Legacy override is null when using preset (preset wins on backend anyway)
    invoice_tax_rate_override: null,
    material_markup_pct: markupOverride === '' ? null : parseFloat(markupOverride),
    // Phase C (Feb 2026, QuickBooks parity)
    invoice_note_to_customer: noteToCustomer || null,
    invoice_memo_on_statement: memoOnStatement || null,
    invoice_labor_qb_class_id: laborClassId || null,
    invoice_labor_qb_class_name: laborClassName || null,
    // Review & Push (Feb 2026) — PO + Work Order ride here so they
    // persist to the Job document before the push dialog opens.
    customer_po_number: poNumber || '',
    work_order_number: workOrderNumber || '',
    materials: materials
      .filter(m => (m.description || '').trim().length > 0)
      .map(m => ({
        id: (m.id && !String(m.id).startsWith('tmp-')) ? m.id : undefined,
        description: m.description,
        quantity: parseFloat(m.quantity) || 0,
        cost: m.cost === '' || m.cost == null ? null : parseFloat(m.cost),
        unit_price: parseFloat(m.unit_price) || 0,
        markup_pct: m.markup_pct === '' || m.markup_pct == null ? null : parseFloat(m.markup_pct),
        qb_item_id: m.qb_item_id || null,
        qb_item_name: m.qb_item_name || null,
        qb_class_id: m.qb_class_id || null,
        qb_class_name: m.qb_class_name || null,
      })),
    // Optional charges (Feb 2026) — Travel, Fuel Surcharge, Tools &
    // Equipment, Other. Filtered to non-empty + non-zero rows so we
    // never push $0 SalesItemLines to QBO.
    invoice_charges: charges
      .filter(c => (c.description || '').trim().length > 0
        && (parseFloat(c.quantity) || 0) > 0
        && (parseFloat(c.unit_price) || 0) > 0)
      .map(c => ({
        id: (c.id && !String(c.id).startsWith('tmp-')) ? c.id : undefined,
        kind: c.kind || 'other',
        description: c.description,
        quantity: parseFloat(c.quantity) || 0,
        unit_price: parseFloat(c.unit_price) || 0,
        qb_item_id: c.qb_item_id || null,
        qb_item_name: c.qb_item_name || null,
        qb_class_id: c.qb_class_id || null,
        qb_class_name: c.qb_class_name || null,
      })),
  });

  const [repeatOpen, setRepeatOpen] = useState(false);
  const overrideInvoiceNumber = async () => {
    const current = data?.job?.ht_invoice_number || '';
    const n = window.prompt('Invoice number for this job (must be unique in your company):', current || data?.job?.next_invoice_number || '');
    if (n == null) return;
    const v = n.trim();
    if (!v || v === current) return;
    const reason = window.prompt('Reason for the change (optional — recorded in Admin → Audit Log):', '') || '';
    try {
      await axios.patch(`${API}/jobs/${job.id}/invoice-fields`, { ht_invoice_number: v, ht_invoice_number_reason: reason }, { headers: { Authorization: `Bearer ${token}` } });
      setData((d) => (d ? { ...d, job: { ...d.job, ht_invoice_number: v } } : d));
      toast({ title: `Invoice number set to ${v}`, description: 'PDF, email, exports and QuickBooks will all use this number.' });
    } catch (e) {
      toast({ title: 'Could not change invoice number', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/jobs/${job.id}/invoice-fields`,
        buildPayload(),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const j = res.data?.job || {};
      setMaterials(Array.isArray(j.materials) ? j.materials.map(m => ({ ...m })) : []);
      setCharges(Array.isArray(j.invoice_charges) ? j.invoice_charges.map(c => ({ ...c })) : []);
      setData(prev => ({ ...prev, job: { ...prev?.job, ...j }, totals: res.data?.totals }));
      return true;
    } catch (err) {
      toast({
        title: 'Could not save invoice draft',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOnly = async () => {
    const ok = await saveDraft();
    if (ok) toast({ title: 'Invoice draft saved' });
  };

  const [showQbReview, setShowQbReview] = useState(false);

  const handleSendToQuickBooks = async () => {
    // Save the draft first so PO/Work Order/notes edits are persisted,
    // then open the Review & Push dialog. The dialog itself calls
    // /qb/sync-job once the admin confirms.
    const ok = await saveDraft();
    if (!ok) return;
    setShowQbReview(true);
  };

  const handleDownloadXlsx = async () => {
    const ok = await saveDraft();
    if (!ok) return;
    setBusyAction('xlsx');
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/invoice-lines.xlsx`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Invoice-${job.job_number || job.id?.slice(0, 8)}.xlsx`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'XLSX export failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusyAction(null); }
  };

  const handleDownloadPdf = async () => {
    const ok = await saveDraft();
    if (!ok) return;
    setBusyAction('pdf');
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/invoice-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${job.job_number || job.id?.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'Invoice PDF downloaded' });
      onInvoiced?.({ kind: 'pdf' });
    } catch (err) {
      toast({
        title: 'PDF download failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusyAction(null);
    }
  };

  const handleEmailPdf = async () => {
    const ok = await saveDraft();
    if (!ok) return;
    const to = data?.customer?.email;
    if (!to) {
      toast({
        title: 'No customer email on file',
        description: 'Add a customer email or use Download PDF to send manually.',
        variant: 'destructive',
      });
      return;
    }
    setBusyAction('email');
    try {
      await axios.post(
        `${API}/jobs/${job.id}/invoice-pdf/email`,
        { include_photo_report: true, include_photos_zip: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Invoice emailed', description: `Sent to ${to}` });
      onInvoiced?.({ kind: 'email' });
      onClose?.();
    } catch (err) {
      toast({
        title: 'Email failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusyAction(null);
    }
  };

  const qbConnected = !!data?.qb_connected;
  const qbAlreadyInvoiced = !!data?.job?.qb_invoice_id;
  const customerEmail = data?.customer?.email;
  const anyBusy = saving || busyAction !== null;
  // Currency from the company profile (CAD for Canadian companies, USD default)
  const currency = currencyOverride || data?.company?.currency || 'USD';
  const money = (n) => moneyIn(n, currency);
  const jobAddrHint = data?.job?.state_province
    ? `${data.job.state_province}${data.job.country ? `, ${data.job.country}` : ''}`
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !anyBusy) onClose?.(); }}>
      <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto bg-[#0F1923] border-[#2D3748] text-white" data-testid="invoice-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white flex-wrap">
            <FileText className="w-5 h-5 text-[#1E88E5]" />
            <span>Bill out {job?.job_number ? `· ${job.job_number}` : ''}</span>
            {/* Currency pill — surfaces what currency this invoice will print
                in. Especially important for multi-country businesses (e.g.,
                Canadian crew billing a US site in USD). */}
            {data?.company?.currency && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-[#0B131F] border-[#1E88E5] text-[#1E88E5]"
                title={`Invoice will print amounts in ${currency}`}
                data-testid="invoice-currency-pill"
              >
                {currency}{presentation === 'lump_sum' ? ' · LUMP SUM' : ''}
              </span>
            )}
            {data?.job?.invoice_status && (
              <span className="text-xs font-normal px-2 py-0.5 rounded border capitalize"
                style={data.job.invoice_status === 'draft'
                  ? { background: 'rgba(245,158,11,0.15)', color: '#FBBF24', borderColor: 'rgba(245,158,11,0.4)' }
                  : { background: 'rgba(16,185,129,0.15)', color: '#34D399', borderColor: 'rgba(16,185,129,0.4)' }}
                title={data.job.invoice_status === 'draft' ? 'Auto-created when the estimate job completed — review currency, presentation and items, then send.' : `Invoice ${data.job.invoice_status}`}
                data-testid="invoice-lifecycle-badge">
                {data.job.invoice_status === 'draft' ? 'Draft — auto-created from estimate' : `Invoice ${data.job.invoice_status}`}
              </span>
            )}
            {data?.job?.status && data.job.status !== 'completed' && (
              <span className="text-xs font-normal px-2 py-0.5 rounded bg-amber-700/40 text-amber-200 border border-amber-700/60" data-testid="invoice-status-badge">
                Draft · {data.job.status.replace('_', ' ')}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {job?.title || 'Job'} — fill in billing details, then choose how to send the invoice.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading job billing details…
          </div>
        ) : (
          <div className="space-y-5">
            {/* Invoice number — allocated once from Settings → Invoice Template numbering, then reused */}
            <div className="flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 border border-[#2D3748] bg-[#0B131F]" data-testid="invoice-number-row">
              <Hash className="w-4 h-4 text-[#1E88E5]" />
              <span className="text-xs uppercase tracking-wider text-slate-400">Invoice #</span>
              {data?.job?.ht_invoice_number ? (
                <>
                  <span className="font-semibold text-white" data-testid="invoice-number-value">{data.job.ht_invoice_number}</span>
                  <span className="text-[11px] text-slate-500">assigned — every download, email and QuickBooks push uses this number</span>
                  {data?.job?.qb_invoice_id && (data.job.qb_invoice_doc_number || '') && data.job.qb_invoice_doc_number !== data.job.ht_invoice_number && (
                    <>
                      <span className="text-[11px] text-amber-400" data-testid="invoice-number-qbo-mismatch">QuickBooks has #{data.job.qb_invoice_doc_number}</span>
                      <UpdateQboNumberButton job={{ id: job.id, ...data.job }} onDone={(nj) => setData((d) => (d ? { ...d, job: { ...d.job, qb_invoice_doc_number: nj.qb_invoice_doc_number } } : d))} />
                    </>
                  )}
                </>
              ) : (
                <>
                  <span className="font-semibold text-white" data-testid="invoice-number-next">Next: {data?.job?.next_invoice_number || '—'}</span>
                  <span className="text-[11px] text-slate-500">assigned when you first download, email or push this invoice, then kept</span>
                </>
              )}
              <button
                type="button"
                onClick={() => setRepeatOpen(true)}
                className="text-[11px] underline text-slate-400 hover:text-white inline-flex items-center gap-1"
                title="Create this invoice again on a schedule (new completed job copy each run)"
                data-testid="invoice-repeat-btn"
              >
                <Repeat className="w-3 h-3" /> Repeat invoice…
              </button>
              <button
                type="button"
                onClick={overrideInvoiceNumber}
                className="ml-auto text-[11px] underline text-slate-400 hover:text-white"
                title="Set a specific invoice number for this job (e.g. to match your accounting program)"
                data-testid="invoice-number-edit"
              >
                {data?.job?.ht_invoice_number ? 'Change' : 'Set manually'}
              </button>
            </div>
            {repeatOpen && (
              <RepeatInvoiceDialog kind="job" inv={{ id: job.id, job_number: data?.job?.job_number || job.job_number, title: job.title }} token={token}
                onClose={() => setRepeatOpen(false)}
                onCreated={(s) => { setRepeatOpen(false); toast({ title: `Repeating ${s.source_job_number || 'job'}`, description: `First run ${s.next_run} · ${s.frequency}. A new completed job is created each run; admins are emailed to review and send.` }); }} />
            )}
            {/* Rate + Billing Mode */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="invoice-rate-block">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Billing mode</Label>
                <select
                  value={billingMode}
                  onChange={(e) => setBillingMode(e.target.value)}
                  className="w-full mt-1 rounded-md bg-[#1A2433] border border-[#2D3748] text-white px-3 py-2 text-sm"
                  data-testid="invoice-billing-mode"
                >
                  <option value="hourly">Hourly (rate × hours)</option>
                  <option value="flat">Flat fee</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">
                  {billingMode === 'flat' ? 'Flat fee' : 'Rate ($/hr)'}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={serviceRate}
                  onChange={(e) => setServiceRate(e.target.value)}
                  placeholder="0.00"
                  className="mt-1 bg-[#1A2433] border-[#2D3748] text-white"
                  data-testid="invoice-rate-input"
                />
                {billingMode === 'hourly' && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Hours on file: <strong className="text-slate-300">{Number(data?.job?.total_hours_worked || 0).toFixed(2)}h</strong>
                  </p>
                )}
              </div>
            </div>

            {/* Tax preset selector (Phase 2) */}
            <div data-testid="invoice-tax-block">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Tax (regional preset)</Label>
                {jobAddrHint && (
                  <span className="text-[11px] text-slate-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Job: {jobAddrHint}
                  </span>
                )}
              </div>
              <Select value={taxPresetId || 'none'} onValueChange={setTaxPresetId}>
                <SelectTrigger className="bg-[#1A2433] border-[#2D3748] text-white" data-testid="invoice-tax-preset-select">
                  <SelectValue placeholder="Select a tax preset" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="none">No preset (use company default)</SelectItem>
                  {(data?.tax_presets || []).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {data?.suggested_tax_preset && !data?.job?.invoice_tax_preset_id && taxPresetId === data.suggested_tax_preset.id && (
                <p className="text-[11px] text-blue-300/80 mt-1 flex items-center gap-1" data-testid="invoice-tax-suggested-hint">
                  <Info className="w-3 h-3" /> Auto-suggested from job address: {data.suggested_tax_preset.name}
                </p>
              )}
            </div>

            {/* Markup banner (Phase 2) */}
            <div className="rounded-lg border border-[#2D3748] bg-[#0B131F] p-3 flex items-start gap-3" data-testid="invoice-markup-block">
              <Percent className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-xs text-slate-300">
                    Material markup: <strong className="text-white">{effectiveMarkup}%</strong>
                    <span className="text-slate-500 ml-1.5">from {markupSource}</span>
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="invoice-markup-override" className="text-[11px] text-slate-400 whitespace-nowrap">Override:</Label>
                    <Input
                      id="invoice-markup-override"
                      type="number"
                      step="0.1"
                      min="0"
                      value={markupOverride}
                      onChange={(e) => setMarkupOverride(e.target.value)}
                      placeholder="—"
                      className="w-20 h-7 bg-[#1A2433] border-[#2D3748] text-white text-xs"
                      data-testid="invoice-markup-override-input"
                    />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  Editing Cost on a row auto-fills Unit Price using this markup. Per-row markup column overrides for that line only.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-[#2D3748] p-3" data-testid="invoice-columns-block">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Invoice column headers</Label>
                  <p className="text-[11px] text-slate-500">As printed on this job's invoice PDF and XLSX. Click a header to rename it for this invoice only.</p>
                </div>
                {Object.keys(colLabels).length > 0 && (
                  <button type="button" onClick={() => setColLabels({})} className="text-[11px] text-sky-400 hover:underline" data-testid="invoice-columns-reset">Reset to customer/company headers</button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {docColumns.map((c) => (
                  <div key={c.key} className="px-2.5 py-1.5 rounded-md text-[11px] uppercase tracking-wide font-semibold" style={{ background: '#0F1923', border: `1px solid ${colLabels[c.key] ? '#8BC34A' : '#2D3748'}`, color: '#8BC34A', minWidth: 90 }} data-testid={`invoice-colchip-${c.key}`}>
                    <EditableHeader col={c} testPrefix="invoice-col" onRename={(key, label) => setColLabels((m) => ({ ...m, [key]: label }))} />
                  </div>
                ))}
              </div>
            </div>

            <InvoiceMaterialsEditor
              materials={materials}
              visibleMaterials={visibleMaterials}
              filterByWorker={filterByWorker}
              setFilterByWorker={setFilterByWorker}
              addMaterial={addMaterial}
              removeMaterial={removeMaterial}
              updateMaterial={updateMaterial}
              effectiveMarkup={effectiveMarkup}
              money={money}
              qbItems={qbItems}
              qbClasses={qbClasses}
              columns={docColumns}
            />

            <InvoiceChargesEditor
              charges={charges}
              chargeSuggestions={chargeSuggestions}
              chargeMenuOpen={chargeMenuOpen}
              setChargeMenuOpen={setChargeMenuOpen}
              addCharge={addCharge}
              removeCharge={removeCharge}
              updateCharge={updateCharge}
              money={money}
              columns={docColumns}
            />
            {docColumns.some((c) => c.source === 'custom') && (
              <div className="rounded-lg border border-[#2D3748] p-3" data-testid="invoice-labour-custom-block">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Labour line — extra columns</Label>
                <p className="text-[11px] text-slate-500 mb-1">Values printed in your custom invoice columns for the labour / service line.</p>
                <CustomColumnInputs columns={docColumns} values={labourCustom} onChange={setLabourCustom} testIdPrefix="invoice-labour-cf" />
              </div>
            )}

            {/* Memo */}
            <InvoiceCurrencyPresentation
              currency={data?.company?.currency || 'USD'}
              currencySource={data?.company?.currency_source}
              currencyOptions={data?.company?.currency_options}
              currencyOverride={currencyOverride}
              setCurrencyOverride={setCurrencyOverride}
              presentation={presentation}
              setPresentation={setPresentation}
              summary={summary}
              setSummary={setSummary}
              jobTitle={job?.title}
            />

            <div data-testid="invoice-memo-block">
              <Label htmlFor="invoice-memo" className="text-xs uppercase tracking-wider text-slate-400">Memo / notes on invoice <span className="text-[10px] normal-case text-slate-500 ml-1">(legacy — superseded by Note to Customer below)</span></Label>
              <Textarea
                id="invoice-memo"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="Visible to the customer (e.g., Thank you for your business. PO #12345.)"
                className="mt-1 bg-[#1A2433] border-[#2D3748] text-white text-sm min-h-[70px]"
                data-testid="invoice-memo-input"
              />
            </div>

            <InvoiceQbParityBlock
              poNumber={poNumber}
              setPoNumber={setPoNumber}
              workOrderNumber={workOrderNumber}
              setWorkOrderNumber={setWorkOrderNumber}
              noteToCustomer={noteToCustomer}
              setNoteToCustomer={setNoteToCustomer}
              memoOnStatement={memoOnStatement}
              setMemoOnStatement={setMemoOnStatement}
              laborClassId={laborClassId}
              setLaborClassId={setLaborClassId}
              setLaborClassName={setLaborClassName}
              qbClasses={qbClasses}
            />

            {/* Worker contributions snapshot (Phase 2 — Feb 2026)
                Surfaces who sourced which materials so admin can spot-check
                or use it for commission/payout decisions. Only renders when
                at least one material has `added_by_name`. */}
            {workerContributions.length > 0 && (
              <div className="rounded-lg border border-[#2D3748] bg-[#0B131F] p-3" data-testid="invoice-worker-contributions">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs uppercase tracking-wider text-slate-400">Materials sourced by</span>
                  <span className="text-[10px] text-slate-500">Click a name to filter · Cost basis</span>
                </div>
                <div className="space-y-1">
                  {workerContributions.map((w) => {
                    const isActive = filterByWorker === w.name;
                    return (
                      <button
                        type="button"
                        key={w.name}
                        onClick={() => setFilterByWorker(isActive ? null : w.name)}
                        className={`w-full flex items-center justify-between text-sm px-2 py-1 rounded transition-colors ${
                          isActive
                            ? 'bg-[#1E88E5]/15 ring-1 ring-[#1E88E5]'
                            : 'hover:bg-[#1A2433]'
                        }`}
                        data-testid={`invoice-contrib-${w.name.replace(/\s+/g,'-')}`}
                        title={isActive ? 'Showing only this worker — click again to show all' : `Filter materials to show only ${w.name}'s entries`}
                      >
                        <span className="text-slate-200 flex items-center gap-1.5">
                          {w.name}
                          <span className="text-[10px] text-slate-500">
                            ({w.count} item{w.count === 1 ? '' : 's'})
                          </span>
                          {isActive && <span className="text-[10px] text-[#1E88E5] font-semibold">· filtering</span>}
                        </span>
                        <span className="text-emerald-300 font-medium">{money(w.total)}</span>
                      </button>
                    );
                  })}
                </div>
                {filterByWorker && (
                  <button
                    type="button"
                    onClick={() => setFilterByWorker(null)}
                    className="mt-2 text-[11px] text-[#1E88E5] hover:underline"
                    data-testid="invoice-contrib-clear-filter"
                  >
                    × Clear filter — show all materials
                  </button>
                )}
              </div>
            )}

            {/* Live totals (Phase 2 — multi-line tax) */}
            {localTotals && (
              <div className="rounded-lg border border-[#2D3748] bg-[#0B131F] p-4 space-y-1.5" data-testid="invoice-totals-card">
                <div className="flex justify-between text-sm text-slate-300">
                  <span>
                    {localTotals.useFlat ? 'Labor (flat)' : `Labor (${localTotals.hours.toFixed(2)}h × ${money(parseFloat(serviceRate) || 0)})`}
                  </span>
                  <span data-testid="invoice-total-labor">{money(localTotals.labor)}</span>
                </div>
                {localTotals.materialsTotal > 0 && (
                  <div className="flex justify-between text-sm text-slate-300">
                    <span>Materials</span>
                    <span data-testid="invoice-total-materials">{money(localTotals.materialsTotal)}</span>
                  </div>
                )}
                {/* Net cost basis (internal margin hint) — only shows when at
                    least one material has a `cost` set. Helps admin sanity-check
                    profit before sending. Italics + dim so it reads as a note,
                    not part of the customer-facing totals. */}
                {localTotals.materialsCostTotal > 0 && (
                  <div className="flex justify-between text-xs text-slate-500 italic" data-testid="invoice-total-cost-basis">
                    <span>
                      Cost basis (internal)
                      {(() => {
                        const margin = localTotals.materialsTotal - localTotals.materialsCostTotal;
                        const marginPct = localTotals.materialsCostTotal > 0
                          ? +(margin / localTotals.materialsCostTotal * 100).toFixed(1)
                          : 0;
                        if (margin <= 0) {
                          return <span className="ml-1.5 text-rose-400">(billed at cost — no margin)</span>;
                        }
                        return <span className="ml-1.5 text-emerald-400">(margin {money(margin)} · {marginPct}%)</span>;
                      })()}
                    </span>
                    <span>{money(localTotals.materialsCostTotal)}</span>
                  </div>
                )}
                {localTotals.chargesTotal > 0 && (
                  <div className="flex justify-between text-sm text-slate-300">
                    <span>Optional charges</span>
                    <span data-testid="invoice-total-charges">{money(localTotals.chargesTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-slate-300 border-t border-[#2D3748] pt-1.5">
                  <span>Subtotal</span>
                  <span data-testid="invoice-total-subtotal">{money(localTotals.subtotal)}</span>
                </div>
                {localTotals.taxComponents.map((c, i) => (
                  <div key={i} className="flex justify-between text-sm text-slate-300" data-testid={`invoice-total-tax-component-${i}`}>
                    <span>{c.label} ({c.rate_pct}%)</span>
                    <span>{money(c.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-base font-bold text-[#1E88E5] border-t border-[#2D3748] pt-1.5 mt-1">
                  <span>Total</span>
                  <span data-testid="invoice-total-grand">{money(localTotals.grandTotal)}</span>
                </div>
              </div>
            )}

            {/* QB status banners */}
            {!qbConnected && (
              <div className="flex items-start gap-2 rounded-md bg-amber-950/40 border border-amber-800/60 px-3 py-2 text-xs text-amber-200" data-testid="invoice-qb-disconnected-banner">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  QuickBooks isn't connected. Connect it in <strong>Settings → QuickBooks Online</strong> to enable one-click sync. You can still Download or Email the PDF invoice below.
                </span>
              </div>
            )}
            {qbAlreadyInvoiced && (
              <div className="flex items-start gap-2 rounded-md bg-emerald-950/40 border border-emerald-800/60 px-3 py-2 text-xs text-emerald-200" data-testid="invoice-already-synced-banner">
                <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Already synced to QuickBooks (invoice <strong>#{String(data.job.qb_invoice_id).slice(0, 12)}</strong>). You can still download or email the PDF copy.
                </span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2 mt-4">
          <Button
            variant="outline"
            onClick={handleSaveOnly}
            disabled={anyBusy || loading}
            className="border-[#2D3748] bg-[#1A2433] text-white hover:bg-[#2D3748]"
            data-testid="invoice-save-btn"
          >
            {saving && busyAction === null
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              : <><Save className="w-4 h-4 mr-2" />Save draft</>}
          </Button>

          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={anyBusy || loading}
              className="border-[#1E88E5] text-[#1E88E5] hover:bg-[#1E88E5] hover:text-white"
              data-testid="invoice-download-pdf-btn"
            >
              {busyAction === 'pdf'
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</>
                : <><Download className="w-4 h-4 mr-2" />Download PDF</>}
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadXlsx}
              disabled={anyBusy || loading}
              className="border-[#2D3748] text-slate-200"
              title="Line items as a spreadsheet, using your column layout"
              data-testid="invoice-download-xlsx-btn"
            >
              {busyAction === 'xlsx' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting…</> : <><Download className="w-4 h-4 mr-2" />XLSX lines</>}
            </Button>
            <Button
              variant="outline"
              onClick={handleEmailPdf}
              disabled={anyBusy || loading || !customerEmail}
              title={!customerEmail ? 'Add a customer email to enable' : `Email invoice to ${customerEmail}`}
              className="border-[#00C896] text-[#00C896] hover:bg-[#00C896] hover:text-white disabled:opacity-50"
              data-testid="invoice-email-btn"
            >
              {busyAction === 'email'
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Emailing…</>
                : <><Mail className="w-4 h-4 mr-2" />Email PDF</>}
            </Button>
            <Button
              onClick={handleSendToQuickBooks}
              disabled={anyBusy || loading}
              title={
                qbAlreadyInvoiced
                  ? 'Already invoiced — Review opens the Re-push dialog to update the QBO invoice header'
                  : !qbConnected
                    ? 'Review what would be sent — Push button inside is disabled until QuickBooks is connected'
                    : `Send ${money(localTotals?.grandTotal)} to QuickBooks`
              }
              className="bg-[#2CA01C] hover:bg-[#249118] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="invoice-send-qb-btn"
            >
              {busyAction === 'qb'
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                : qbAlreadyInvoiced
                  ? <><Send className="w-4 h-4 mr-2" />Re-push to QuickBooks</>
                  : <><Send className="w-4 h-4 mr-2" />Review & Push to QuickBooks</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Review & Push to QuickBooks (Feb 2026). Opens when admin clicks
          "Send to QuickBooks" — shows the exact PrivateNote/CustomerMemo
          and lets them pick which optional sections to include. */}
      <QbPushReviewDialog
        open={showQbReview}
        onOpenChange={setShowQbReview}
        job={job}
        qbConnected={qbConnected}
        onPushed={(result) => {
          onInvoiced?.({ kind: 'qb', invoice_id: result?.invoice_id });
          onClose?.();
        }}
      />
    </Dialog>
  );
}
